import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { moneyFormat, queryString, apiWrapper } from '../../helpers/common';
import { subcatIdMap } from '../constants';

const Calendar = loadable(() => import('../../common/Calendar'));
const Checkbox = loadable(() => import('../../common/Checkbox'));
const Radio = loadable(() => import('../../common/Radio'));
const Switch = loadable(() => import('../../common/Switch'));
const Select = loadable(() => import('../../common/Select'));
const TextField = loadable(() => import('../../common/TextField'));
const Chip = loadable(() => import('../../common/Chip'));
const UploadImages = loadable(() => import('../UploadImages'));
const ModalOpener = loadable(() => import('../ModalOpener'));
const Accordion = loadable(() => import('../Accordion'));
const SelectSuggest = loadable(() => import('../SelectSuggest'));

const AddedMargin = styled.div`
  margin: 10px 0px;
`;

const InputComponent = ({
  component,
  id,
  label,
  type,
  options = [],
  section,
  validation,
  parent,
  prefix,
  inputState,
  setInputState,
  defaultvalue,
  disabled,
  child,
  subcategory,
  dynamic,
  childOptions,
  setChildOptions,
  loadDynamicOptions = () => {},
  isMobile,
  attrid,
  fireGa,
  ...rest
}) => {
  const { watch, getValues, setValue, unregister } = useFormContext();
  let parentSelectedValue = parent && watch(parent);
  if (parentSelectedValue && typeof parentSelectedValue === 'object') {
    parentSelectedValue = parentSelectedValue.id;
  }
  const value = getValues(id);
  validation =
    validation && validation.pattern
      ? {
          ...validation,
          pattern: {
            value: new RegExp(`${validation.pattern.value}`),
            message: validation.pattern.message
          }
        }
      : validation;
  const price = id === 'price' ? watch('price') : 0;
  const helpText = price > 0 ? moneyFormat.convert(price) : '';
  const dynamicOptions = /city|locality/.test(id) && watch('dynamicOptions');
  let items = (dynamicOptions && dynamicOptions[id]) || (options || []).slice();
  const children = (child && child.split(',')) || [];
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (defaultvalue || queryParams.get(id)) {
      // for setting default values on load
      const parsedVal = defaultvalue ? JSON.parse(defaultvalue) : queryParams.get(id);
      setValue(id, parsedVal);
      const selectedOption = items && items.find(item => item.value === parsedVal);
      setInputState &&
        selectedOption &&
        setInputState(state => ({
          ...state,
          [id]: { [parsedVal]: selectedOption.id }
        }));
    }
  }, []);

  useEffect(() => {
    if (
      parent &&
      parentSelectedValue &&
      dynamic &&
      inputState &&
      (!(childOptions[parent] && childOptions[parent][id]) ||
        Object.keys(childOptions[parent][id]).length === 0)
    ) {
      const attributeValueId = inputState[parent] && inputState[parent][parentSelectedValue];
      const payload = {
        type: 'ATTRIBUTE',
        subcatId: subcatIdMap[subcategory],
        attrId: attrid,
        parentValueId: attributeValueId
      };
      if (!items || !items.length) {
        apiWrapper({
          url: `${__ATTR_CLIENT_ENDPOINT}/attr/postad/getAttributes${queryString(payload)}`,
          headers: {
            'Content-Type': 'application/json'
          }
        })
          .then(response => {
            const optionList = response || {};
            if (Object.keys(optionList).length > 0) {
              setChildOptions(state => ({
                ...state,
                [parent]: {
                  ...(state[parent] || {}),
                  [id]: optionList
                }
              }));
            }
          })
          .catch(errors => {
            console.log(errors);
          });
      } else {
        items = items.filter(i => i.parent === attributeValueId);
      }
    }
  }, [parent, parentSelectedValue, inputState, childOptions]);

  if (parent) {
    if (
      !parentSelectedValue ||
      (dynamic && !((childOptions[parent] && childOptions[parent][id]) || (items && items.length)))
    ) {
      unregister(id);
      return null;
    }
    if (dynamic && inputState) {
      const attrId = inputState[parent] && inputState[parent][parentSelectedValue];
      items = items && items.length ? items.filter(i => i.parent === attrId) : [];
      if (
        !items.length &&
        childOptions[parent] &&
        childOptions[parent][id] &&
        childOptions[parent][id][attrId]
      ) {
        items = childOptions[parent][id][attrId];
      }
    }
    if (items && items.length < 1 && component !== 'autosuggest') {
      unregister(id);
      return null;
    }
  }

  if (component === 'postfilled') {
    if (!value) {
      return null;
    }
  }

  const handleAutoComplete = input => loadDynamicOptions(id, input);

  const handleSelectChange = val => {
    const selectedValue = getValues(children);
    setValue(id, val);
    if (val) {
      const state = { ...inputState };
      if (children.length > 0) {
        children.forEach(each => {
          if (selectedValue[each]) {
            delete state[each];
            setValue(each, undefined);
          }
        });
        setChildOptions(state => ({
          ...state,
          [id]: {}
        }));
      }
      setInputState({
        ...state,
        [id]: { [val]: items.find(item => item.value === val).id }
      });
    }
  };

  switch (component) {
    case 'select':
      return (
        <div className="postad__boxrow">
          <Select
            name={id}
            items={items}
            selectedVal={value}
            labelText={label}
            updateVal={value}
            onChange={handleSelectChange}
            multiple={type === 'multi'}
            validation={validation}
            isRequired={!!(validation.required && validation.required !== '')}
            {...rest}
            disabled={disabled}
            isMobile={isMobile}
          />
        </div>
      );
    case 'chip':
      return (
        <Chip
          name={id}
          items={items}
          label={label}
          labelKey="label"
          valueKey="value"
          validation={validation}
          onChange={handleSelectChange}
          {...rest}
          disabled={disabled}
        />
      );
    case 'textfield':
      return (
        <TextField
          className="postad__boxrow"
          inputName={id}
          labelName={label}
          type={type}
          maxLength={validation.maxLength && +validation.maxLength.value}
          helpText={helpText}
          value={value}
          leftText={prefix}
          validation={validation}
          {...rest}
          disabled={disabled}
        />
      );
    case 'textarea':
      return (
        <TextField
          className="postad__boxrow"
          inputName={id}
          type="text"
          multiline
          rows="4"
          labelName={label}
          validation={validation}
          {...rest}
          disabled={disabled}
        />
      );
    case 'postfilled':
      return (
        <TextField
          className="postad__boxrow"
          inputName={id}
          labelName={label}
          type={type}
          field={component}
          maxLength={validation.maxLength && +validation.maxLength.value}
          helpText={helpText}
          value={id === 'city' ? getValues(`${id}.name`) : value}
          leftText={prefix}
          validation={validation}
          {...rest}
          disabled={disabled}
        />
      );
    case 'modal':
      return (
        <ModalOpener
          data={section}
          className="max__width"
          inputState={inputState}
          setInputState={setInputState}
          subcategory={subcategory}
          childOptions={childOptions}
          setChildOptions={setChildOptions}
          dynamic={dynamic}
        />
      );
    case 'accordion':
      return (
        <Accordion
          data={section}
          className="postad__boxrow"
          inputState={inputState}
          setInputState={setInputState}
          subcategory={subcategory}
          childOptions={childOptions}
          setChildOptions={setChildOptions}
          dynamic={dynamic}
          isMobile={isMobile}
          fireGa={fireGa}
          {...rest}
        />
      );
    case 'autosuggest':
      return (
        <div className="postad__boxrow">
          {disabled ? (
            <Select
              name={id}
              items={items}
              selectedVal={value}
              labelText={label}
              updateVal={value}
              onChange={handleSelectChange}
              multiple={type === 'multi'}
              validation={validation}
              {...rest}
              disabled={true}
              isMobile={isMobile}
            />
          ) : (
            <SelectSuggest
              name={id}
              items={items}
              labelText={label}
              suggestFor={id} // city/locality
              handleAutoComplete={handleAutoComplete}
              validation={validation}
              isMobile={isMobile}
              {...rest}
            />
          )}
        </div>
      );
    case 'images':
      return (
        <UploadImages
          name={id}
          adviceMessage={label}
          validation={validation}
          {...rest}
          defaultvalue={defaultvalue}
          disabled={disabled}
          isMobile={isMobile}
        />
      );
    case 'calendar':
      return (
        <Calendar
          className="postad__boxrow"
          name={id}
          labelName={label}
          validation={validation}
          {...rest}
          disabled={disabled}
        />
      );
    case 'checkbox':
      return (
        <AddedMargin>
          <Checkbox name={id} validation={validation} {...rest} value={label} disabled={disabled} />
        </AddedMargin>
      );
    case 'radio':
      return (
        <AddedMargin>
          <Radio name={id} validation={validation} {...rest} value={label} disabled={disabled} />
        </AddedMargin>
      );
    case 'switch':
      return (
        <AddedMargin>
          <Switch name={id} validation={validation} {...rest} value={label} disabled={disabled} />
        </AddedMargin>
      );
    default:
      return null;
  }
};

InputComponent.propTypes = {
  disabled: PropTypes.bool,
  component: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.string,
  parent: PropTypes.string,
  child: PropTypes.string,
  prefix: PropTypes.string,
  defaultvalue: PropTypes.string,
  options: PropTypes.array,
  section: PropTypes.object,
  inputState: PropTypes.object,
  validation: PropTypes.object,
  setInputState: PropTypes.func,
  loadDynamicOptions: PropTypes.func,
  isMobile: PropTypes.bool,
  fireGa: PropTypes.func
};

export default InputComponent;
